import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";

import {
  DataConnector,
  DataConnectorStatus,
  DataConnectorType,
} from "@/api/data-connectors";
import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import { Select } from "@/design-system/components/Select/Select";
import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import PageWithTitle from "@/PageWithTitle";

import { useDataConnectors, useSetDataConnectorStatus } from "../api";
import { DataConnectorBuilder } from "../components/DataConnectorBuilder/DataConnectorBuilder";
import { DataConnectorDisableConfirmation } from "../components/DataConnectorDisableConfirmation";
import {
  DataConnectorList,
  DataConnectorListSkeleton,
} from "../components/DataConnectorList";
import { DataConnectorListEmptyState } from "../components/DataConnectorListEmptyState";

type SortOption = "last_modified" | "created" | "name";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "last_modified", label: "Last modified" },
  { value: "created", label: "Created" },
  { value: "name", label: "Name" },
];

const STATUS_FILTERS: { value: DataConnectorStatus | "all"; label: string }[] =
  [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "disabled", label: "Disabled" },
  ];

const TYPE_FILTERS: { value: DataConnectorType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "api", label: "API" },
  { value: "airbyte", label: "Airbyte" },
];

function sortDataConnectors(
  items: DataConnector[],
  sortBy: SortOption,
): DataConnector[] {
  return [...items].sort((a, b) => {
    switch (sortBy) {
      case "last_modified":
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      case "created":
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case "name":
        return a.name.localeCompare(b.name);
    }
  });
}

export default function DataConnectorsListPage() {
  useSetBreadcrumbs([{ label: "Integrations" }, { label: "Data Connectors" }]);

  const { data: dataConnectors, isLoading } = useDataConnectors();
  const setStatusMutation = useSetDataConnectorStatus();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("last_modified");
  const [statusFilter, setStatusFilter] = useState<DataConnectorStatus | "all">(
    "all",
  );
  const [typeFilter, setTypeFilter] = useState<DataConnectorType | "all">(
    "all",
  );
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<DataConnector | undefined>(undefined);
  const [pendingDisable, setPendingDisable] = useState<
    DataConnector | undefined
  >(undefined);

  const filtered = useMemo(() => {
    let items = dataConnectors ?? [];
    if (statusFilter !== "all") {
      items = items.filter((d) => d.status === statusFilter);
    }
    if (typeFilter !== "all") {
      items = items.filter((d) => d.type === typeFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    const matched = q
      ? items.filter((d) => d.name.toLowerCase().includes(q))
      : items;
    return sortDataConnectors(matched, sortBy);
  }, [dataConnectors, searchQuery, sortBy, statusFilter, typeFilter]);

  const handleSetStatus = (dc: DataConnector, status: DataConnectorStatus) => {
    if (status === "disabled") {
      setPendingDisable(dc);
      return;
    }
    setStatusMutation.mutate({ id: dc.id, status });
  };

  const confirmDisable = () => {
    if (!pendingDisable) return;
    setStatusMutation.mutate(
      { id: pendingDisable.id, status: "disabled" },
      { onSuccess: () => setPendingDisable(undefined) },
    );
  };

  const openCreate = () => {
    setEditing(undefined);
    setBuilderOpen(true);
  };

  const openEdit = (dc: DataConnector) => {
    setEditing(dc);
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setEditing(undefined);
  };

  const total = dataConnectors?.length ?? 0;

  return (
    <PageWithTitle title="Data connectors">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Data connectors
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Connect your data from external systems to Freshpaint
            </p>
          </div>
          <Button onClick={openCreate} leadingIcon={<Plus size={16} />}>
            Create data connector
          </Button>
        </div>

        {total > 0 && (
          <div className="mb-4 flex w-full flex-wrap items-center gap-4">
            <Input
              type="search"
              placeholder="Search data connectors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leadingIcon={
                <Search size={16} className="shrink-0 text-gray-400" />
              }
              className="h-9 w-full max-w-[248px] py-0"
            />
            <Select
              options={STATUS_FILTERS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              insetLabel="Status:"
              rootProps={{
                value: statusFilter,
                onValueChange: (v) =>
                  setStatusFilter(v as DataConnectorStatus | "all"),
              }}
              triggerProps={{
                className:
                  "h-9 w-auto gap-1.5 rounded-lg px-4 py-0 text-sm inline-flex items-center",
              }}
              contentProps={{ className: "text-sm" }}
            />
            <Select
              options={TYPE_FILTERS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              insetLabel="Type:"
              rootProps={{
                value: typeFilter,
                onValueChange: (v) =>
                  setTypeFilter(v as DataConnectorType | "all"),
              }}
              triggerProps={{
                className:
                  "h-9 w-auto gap-1.5 rounded-lg px-4 py-0 text-sm inline-flex items-center",
              }}
              contentProps={{ className: "text-sm" }}
            />
            <Select
              options={SORT_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              insetLabel="Sort:"
              rootProps={{
                value: sortBy,
                onValueChange: (v) => setSortBy(v as SortOption),
              }}
              triggerProps={{
                className:
                  "h-9 w-auto gap-1.5 rounded-lg px-4 py-0 text-sm inline-flex items-center",
              }}
              contentProps={{ className: "text-sm" }}
            />
          </div>
        )}

        {isLoading ? (
          <DataConnectorListSkeleton />
        ) : filtered.length === 0 ? (
          <DataConnectorListEmptyState
            isFiltered={total > 0}
            onCreateNew={openCreate}
          />
        ) : (
          <DataConnectorList
            dataConnectors={filtered}
            onEdit={openEdit}
            onSetStatus={handleSetStatus}
          />
        )}
      </div>
      <DataConnectorBuilder
        open={builderOpen}
        onClose={closeBuilder}
        dataConnector={editing}
      />
      <DataConnectorDisableConfirmation
        dataConnector={pendingDisable}
        onConfirm={confirmDisable}
        onCancel={() => setPendingDisable(undefined)}
        isPending={setStatusMutation.isLoading}
      />
    </PageWithTitle>
  );
}
